/**
 * @module PageErrorBoundary
 * @package @geenius/adapters/react-css
 * @description Shared page-level error boundary for the React CSS adapter
 * pages. Mirrors the Tailwind React variant's recovery contract but emits the
 * existing `gn-adapters-alert` BEM classes so the CSS-only build can style it
 * without Tailwind.
 */

"use client";

import { Component, type ErrorInfo, type ReactNode } from "react";
import { cx } from "../lib/cx";

interface PageErrorBoundaryProps {
  children: ReactNode;
  className?: string;
  title: string;
}

interface PageErrorBoundaryState {
  error: Error | null;
}

export class PageErrorBoundary extends Component<
  PageErrorBoundaryProps,
  PageErrorBoundaryState
> {
  state: PageErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): PageErrorBoundaryState {
    return { error };
  }

  componentDidCatch(_error: Error, _info: ErrorInfo): void {
    // React requires this hook for class error boundaries; callers handle logging.
  }

  render(): ReactNode {
    if (!this.state.error) {
      return this.props.children;
    }

    const message =
      this.state.error.message.trim().length > 0
        ? this.state.error.message
        : "The adapter page could not finish rendering.";

    return (
      <div
        className={cx(
          "gn-adapters-page",
          "gn-adapters-page--narrow",
          this.props.className,
        )}
      >
        <div
          className="gn-adapters-alert gn-adapters-alert--error gn-adapters-alert--spaced"
          role="alert"
          aria-live="assertive"
        >
          <strong>{this.props.title}</strong>
          <p>{message}</p>
          <button
            type="button"
            className="gn-adapters-btn gn-adapters-btn--primary"
            onClick={() => this.setState({ error: null })}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }
}
